import React, { useEffect, useState, useRef } from "react";
import { ChevronRight, Copy, Check, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * OffersStrip — SmartBuy-style promo carousel (Offers slice).
 *
 * SmartBuy layout mechanics (deep dark hero, 3D illo on the right, big
 * "%×" badge, dot-pagination) on the Find2Near palette
 * (forest green `#2D5A27`, sunset orange `#E05E36`, sand `#DDA77B`).
 *
 * Data source: `GET /api/offers/active?scope=&category=`. Slot on Search page
 * top-of-fold; auto-hides when the endpoint returns zero rows.
 */
export default function OffersStrip({
  scope = "search",
  category = null,
  className = "",
  testid = "offers-strip",
}) {
  const [offers, setOffers] = useState(null);
  const [idx, setIdx]       = useState(0);
  const [copied, setCopied] = useState(null);
  const timerRef            = useRef(null);

  useEffect(() => {
    const params = { scope, limit: 5 };
    if (category) params.category = category;
    api.get("/offers/active", { params })
      .then(r => setOffers(r.data?.offers || []))
      .catch(() => setOffers([]));
  }, [scope, category]);

  useEffect(() => {
    if (!offers?.length || offers.length < 2) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(
      () => setIdx(i => (i + 1) % offers.length),
      6500
    );
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [offers?.length]);

  if (offers === null) return null;   // loading — silent
  if (offers.length === 0) return null;  // auto-hide

  const cur = offers[idx];
  const copyCode = async () => {
    if (!cur.promo_code) return;
    try {
      await navigator.clipboard.writeText(cur.promo_code);
      setCopied(cur.offer_id);
      toast.success(`Code ${cur.promo_code} copied`);
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div
      className={`relative rounded-3xl overflow-hidden ${className}`}
      data-testid={testid}
      data-offer-count={offers.length}
    >
      {/* Deep-navy hero mirroring the SmartBuy layout, on Find2Near palette */}
      <div
        className="relative min-h-[220px] sm:min-h-[260px] p-6 sm:p-8 text-white"
        style={{
          background:
            "radial-gradient(600px 300px at 90% 20%, rgba(224,94,54,0.35), transparent 70%), " +
            "linear-gradient(135deg, #0f1b16 0%, #14322a 45%, #163b30 100%)",
        }}
        data-testid={`${testid}-slide-${cur.offer_id}`}
      >
        <div className="flex items-start justify-between gap-4 h-full">
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            {cur.sponsor_name && (
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-white/70">
                {cur.sponsor_logo && (
                  <img loading="lazy" src={cur.sponsor_logo} alt="" className="w-4 h-4 rounded" />
                )}
                {cur.sponsor_name}
              </div>
            )}
            <div className="mt-3">
              <h3 className="heading text-2xl sm:text-3xl font-black tracking-tight" data-testid="offer-title">
                {cur.title}
              </h3>
              {cur.subtitle && (
                <p className="mt-1 text-sm text-white/80" data-testid="offer-subtitle">
                  {cur.subtitle}
                </p>
              )}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <a
                href={cur.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => api.post(`/offers/${cur.offer_id}/redeem`).catch(() => {})}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white font-bold px-4 py-2 text-sm"
                data-testid={`offer-cta-${cur.offer_id}`}
              >
                {cur.cta_text || "Order now"} <ExternalLink size={12} />
              </a>
              {cur.promo_code && (
                <button
                  type="button"
                  onClick={copyCode}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white/95 font-mono text-xs px-3 py-2"
                  data-testid={`offer-code-${cur.offer_id}`}
                  title="Tap to copy"
                >
                  {copied === cur.offer_id ? <Check size={12} /> : <Copy size={12} />}
                  {cur.promo_code}
                </button>
              )}
              {cur.cashback_pct != null && (
                <span className="text-[10px] font-black tracking-wider uppercase bg-[#2D5A27] border border-white/25 text-white px-2 py-1 rounded">
                  {cur.cashback_pct}% cashback
                </span>
              )}
            </div>
          </div>

          {/* Right column — illustration + big discount badge (SmartBuy 10× style) */}
          <div className="relative shrink-0 w-28 sm:w-40 self-center">
            {cur.image && (
              <img
                loading="lazy"
                src={cur.image}
                alt=""
                className="w-full aspect-square object-cover rounded-2xl border border-white/15"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            {cur.discount_pct != null && (
              <div className="absolute -top-3 -right-3 grid place-items-center w-14 h-14 rounded-full bg-[#E05E36] border-2 border-white shadow-lg">
                <span className="text-white font-black text-sm leading-none text-center px-1">
                  {cur.discount_pct}%<br/>OFF
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dot-pagination */}
        {offers.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="offers-dots">
            {offers.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#DDA77B]" : "w-1.5 bg-white/40"
                }`}
                aria-label={`Offer ${i + 1}`}
                data-testid={`offers-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
      {/* Counter badge — small "1/5" hint like SmartBuy */}
      {offers.length > 1 && (
        <div className="absolute top-3 right-3 bg-black/40 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-bold text-white/90"
             data-testid="offers-counter">
          {idx + 1}/{offers.length}
        </div>
      )}
    </div>
  );
}
